import React, { useEffect, useState } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	CardMedia,
	CircularProgress,
	Grid2,
	Modal,
	Typography,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import supabase from "../../supabaseClient";
import { getImageUrl } from "../../utils/imageUrl";
import { createPlantSlug } from "../../utils/slugify";
import ReactGA from "react-ga4";

const InStock = () => {
	// Derive price from the first active linked store item (price_cents → dollars).
	const getPlantPrice = (plant) => {
		const priceCents =
			plant.store_item_plants?.find((sip) => sip.store_items?.active)
				?.store_items?.price_cents ??
			plant.store_item_plants?.[0]?.store_items?.price_cents;
		return priceCents != null ? priceCents / 100 : null;
	};

	// Derive stock status from the linked store item's quantity_on_hand.
	// Returns "in" (in/low stock) or "out" (out of stock / no store item).
	const getStockStatus = (plant) => {
		const storeItem =
			plant.store_item_plants?.find((sip) => sip.store_items?.active)
				?.store_items ?? plant.store_item_plants?.[0]?.store_items;
		if (!storeItem) return "out";
		return storeItem.quantity_on_hand > 0 ? "in" : "out";
	};

	// Returns quantity and reorder data for a plant's linked store item.
	const getInventory = (plant) => {
		const storeItem =
			plant.store_item_plants?.find((sip) => sip.store_items?.active)
				?.store_items ?? plant.store_item_plants?.[0]?.store_items;
		return storeItem ?? null;
	};

	// Prefer plant photo; fall back to linked store item photo when plant photo is not set.
	const getPlantImage = (plant) => {
		if (!plant) return null;
		if (plant.photo_url) return plant.photo_url;
		const activeStoreItem =
			plant.store_item_plants?.find((sip) => sip.store_items?.active)
				?.store_items ?? plant.store_item_plants?.[0]?.store_items;
		return activeStoreItem?.photo_url || null;
	};

	const [selectedItem, setSelectedItem] = React.useState(null);
	const [mostRecentUpdate, setMostRecentUpdate] = React.useState("");
	const [loadingStatus, setLoadingStatus] = React.useState("idle");
	const [filter, setFilter] = React.useState("in");
	const [search, setSearch] = React.useState("");
	const [filteredPlants, setFilteredPlants] = React.useState([]);
	const navigate = useNavigate();

	const handleItemClick = (item) => {
		setSelectedItem(item);
	};

	const handleClosePopover = () => {
		setSelectedItem(null);
	};

	const [inStock, setInStock] = React.useState([]);

	useEffect(() => {
		ReactGA.send({
			hitType: "pageview",
			page: "/in-stock",
		});
	}, []);

	// Filter plants based on search and filter criteria, when they change
	useEffect(() => {
		const lowerSearch = search.toLowerCase();
		const filtered = inStock.filter((item) => {
			const matchesSearch =
				item.plant_variety.toLowerCase().includes(lowerSearch) ||
				item.plant_name.toLowerCase().includes(lowerSearch) ||
				item.plant_description.toLowerCase().includes(lowerSearch);
			const stockStatus = getStockStatus(item);
			const matchesFilter =
				filter === "all" ||
				(filter === "in" && stockStatus === "in") ||
				(filter === "out" && stockStatus === "out");
			return matchesSearch && matchesFilter;
		});
		setFilteredPlants(filtered);
	}, [inStock, search, filter]);

	// Load photos from cache or fetch them
	useEffect(() => {
		const fetchInStockPlants = async () => {
			try {
				setLoadingStatus("loading");
				// Fetch plants data first to get mostRecentUpdate
				const { data: plantsData, error } = await supabase
					.from("plants")
					.select(
						"*, store_item_plants(store_items(price_cents, active, quantity_on_hand, reorder_level, photo_url))",
					);

				if (error) {
					console.error("Error fetching plants:", error);
					setLoadingStatus("no-data");
					return;
				}

				// Find the most recent update
				const latestUpdate = plantsData.reduce((latest, plant) => {
					const updatedAt = plant.updated_at || plant.created_at;
					return updatedAt > latest ? updatedAt : latest;
				}, "");

				setMostRecentUpdate(new Date(latestUpdate).toLocaleDateString());

				// Always use fresh data so quantity_on_hand reflects real-time inventory.
				// Clear legacy cache keys to avoid stale stock status from previous versions.
				localStorage.removeItem("inStock_photos_cache");
				localStorage.removeItem("inStock_photos_timestamp");
				setInStock(plantsData || []);
				setLoadingStatus("loaded");
			} catch (error) {
				console.error("Error in fetchInStockPlants:", error);
				setLoadingStatus("no-data");
			}
		};

		fetchInStockPlants();
	}, []);

	//inStockAlphabetical
	inStock.sort((a, b) => (a.plant_variety > b.plant_variety ? 1 : -1));

	const ImageWithLoading = ({ src, alt, width = "100%" }) => {
		const [loading, setLoading] = useState(true);

		return (
			<Box sx={{ position: "relative", width }}>
				{loading && (
					<Box
						sx={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
						}}
					>
						<CircularProgress />
					</Box>
				)}
				<img
					src={getImageUrl(src)}
					alt={alt}
					width={width}
					loading="lazy"
					style={{ display: loading ? "none" : "block" }}
					onLoad={() => setLoading(false)}
					onError={() => setLoading(false)} // Hide loader if image fails to load
				/>
			</Box>
		);
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
				width: "100%",
				padding: "2px",
			}}
		>
			<Helmet>
				<meta
					name="description"
					content="Current in stock items at Gosell Gardens in Inver Grove Heights, MN."
				/>
				<meta name="title" content="Gosell Gardens"></meta>

				<meta property="og:title" content="Gosell Gardens" />
				<meta
					property="og:description"
					content="Current in stock items at Gosell Gardens in Inver Grove Heights, MN."
				/>
				<meta property="og:url" content="https://gosellgardens.com" />
				<meta property="og:type" content="website" />
				<title>Gosell Gardens</title>
			</Helmet>
			<Typography variant="h2" sx={{ mb: 2 }}>
				In Stock
			</Typography>
			{loadingStatus === "loaded" && (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
						width: "100%",
						padding: "2px",
					}}
				>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Here's a list of plants we have prepped and will have available for
						sale on opening day. Please note that availability is subject to
						change. We will update this list as we grow new plants or sell out
						of items!
					</Typography>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Last updated:{` ${mostRecentUpdate}`}
					</Typography>
					<Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
						<ToggleButtonGroup
							value={filter}
							exclusive
							onChange={(_, val) => val && setFilter(val)}
							aria-label="stock filter"
						>
							<ToggleButton value="all">All</ToggleButton>
							<ToggleButton value="in">In Stock</ToggleButton>
							<ToggleButton value="out">Out of Stock</ToggleButton>
						</ToggleButtonGroup>
						<TextField
							label="Search"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							size="small"
						/>
					</Box>
					<Box sx={{ width: "100%" }}>
						<Grid2 container spacing={2}>
							{filteredPlants.length > 0 &&
								filteredPlants.map((item) => (
									<Grid2
										size={{ xs: 12, md: 6 }}
										key={`${item.plant_description} ${item.id} ${item.plant_variety}`}
									>
										<Card
											//make  color of card white
											sx={{
												backgroundColor: "#FFFFFF",
												border: "1px solid #000000",
												borderRadius: "5px",
												marginBottom: "10px",
												//on hover, change the border color and pointer
												"&:hover": {
													border: "1px solid black",
													backgroundColor: "gray",
													cursor: "pointer",
												},
											}}
											key={item.id}
											onClick={() => handleItemClick(item)}
										>
											{getPlantImage(item) && (
												<CardMedia
													component="img"
													height="140"
													//center the image
													image={getImageUrl(getPlantImage(item))}
													alt={item.plant_name}
													loading="lazy"
												/>
											)}
											<CardContent>
												{(() => {
													const inv = getInventory(item);
													if (!inv || inv.quantity_on_hand === 0) {
														return (
															<Typography
																variant="body2"
																color="red"
																sx={{ mb: 1 }}
															>
																Out of stock
															</Typography>
														);
													}
													if (
														inv.quantity_on_hand <= (inv.reorder_level ?? 0)
													) {
														return (
															<Typography
																variant="body2"
																color="warning.main"
																sx={{ mb: 1 }}
															>
																Low stock
															</Typography>
														);
													}
													return null;
												})()}
												<Typography variant="h5" component="div">
													{item.plant_variety}
												</Typography>
												<Typography variant="h6" color="text.main">
													{item.plant_name}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													{getPlantPrice(item) != null
														? `$${getPlantPrice(item).toFixed(2)}`
														: "Price coming soon"}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													{item.plant_description}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													{item.pot_size}
												</Typography>
												<Box
													sx={{ mt: 1 }}
													onClick={(e) => {
														e.stopPropagation();
														navigate(
															`/plants/${item.slug || createPlantSlug(item.plant_variety, item.plant_name)}`,
														);
													}}
												>
													<Button
														variant="outlined"
														size="small"
														component="span"
													>
														View Plant Page
													</Button>
												</Box>
											</CardContent>
										</Card>
									</Grid2>
								))}
							{
								// If no items match the filter, show a message
								filteredPlants.length === 0 && (
									<Grid2 size={{ xs: 12 }}>
										<Typography variant="body2" color="text.secondary">
											No items found matching your criteria.
										</Typography>
									</Grid2>
								)
							}
						</Grid2>
						<Modal
							open={selectedItem !== null}
							onClose={handleClosePopover}
							aria-labelledby="selected-item-modal"
							aria-describedby="selected-item-details"
						>
							<Box
								sx={{
									position: "absolute",
									top: "50%",
									left: "50%",
									transform: "translate(-50%, -50%)",
									bgcolor: "background.paper",
									boxShadow: 24,
									p: 4,
									borderRadius: 2,
									maxWidth: 600,
									width: "90%",
								}}
							>
								<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
									<Close
										onClick={handleClosePopover}
										sx={{ cursor: "pointer" }}
									/>
								</Box>

								{selectedItem && (
									<Box sx={{ p: 2 }}>
										<Grid2 container spacing={2}>
											<Grid2 size={{ xs: 12, md: 6 }}>
												{getPlantImage(selectedItem) ? (
													<ImageWithLoading
														src={getPlantImage(selectedItem)}
														alt={selectedItem.plant_name}
													/>
												) : (
													<Typography variant="body2" color="text.secondary">
														Image coming soon. The plant is still growing!
													</Typography>
												)}
											</Grid2>
											<Grid2 size={{ xs: 12, md: 6 }}>
												<Typography variant="h5" component="div">
													{selectedItem.plant_variety}
												</Typography>
												<Typography variant="h6" color="text.secondary">
													{selectedItem.plant_name}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													{getPlantPrice(selectedItem) != null
														? `$${getPlantPrice(selectedItem).toFixed(2)}`
														: "Price coming soon"}
												</Typography>

												<Typography variant="body2" color="text.secondary">
													{selectedItem.plant_description}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													{selectedItem.pot_size}
												</Typography>
												<Box sx={{ mt: 2 }}>
													<Button
														variant="contained"
														size="small"
														onClick={() => {
															handleClosePopover();
															navigate(
																`/plants/${selectedItem.slug || createPlantSlug(selectedItem.plant_variety, selectedItem.plant_name)}`,
															);
														}}
													>
														View Plant Page
													</Button>
												</Box>
											</Grid2>
										</Grid2>
									</Box>
								)}
							</Box>
						</Modal>
					</Box>
				</Box>
			)}
			{loadingStatus === "loading" && (
				<CircularProgress size={24} sx={{ margin: "auto", display: "block" }} />
			)}
			{loadingStatus === "no-data" && (
				<Typography variant="body2" sx={{ mb: 2 }}>
					No in stock items found. Please check back later.
				</Typography>
			)}
		</Box>
	);
};

export default InStock;
